/* global Sys, Core, Resources, Environment, Mixins */
Sys.ns("Core");

/**
 * The Module Loader.
 *
 * @class Core.ModuleLoader
 * @extends Sys.Observable
 */
Core.ModuleLoader = {
    constructor: function(config) {
        Core.ModuleLoader.superclass.constructor.apply(this, arguments);

        this.init(config);
    },

    /**
     * Returns the ordered queue of functions for a given state.
     *
     * @param {string} stateName The name of the state.
     * @returns {Array} The array containing the functions.
     */
    getModuleQueueForState: function(stateName) {
        var queue = this.stateOrder[stateName];

        if (Sys.isDefined(queue)) {
            return queue;
        }
        return [];
    },

    /**
     * Initialize the module loader.
     *
     * @private
     * @param {Object} config The configuration object.
     * @returns {void}
     */
    init: function(config) {
        var me = this,
            modulesConfig = Resources.readData("modules");

        me.stateHandler = config.stateHandler;

        me.stateOrder = me.getCommonStateOrder();

        me.addGameSpecificStateOrder();

        // Load all modules
        me.loadModules(modulesConfig);

        me.updateStates();

        // Remove unused modules from the state queue
        me.cleanStateQueue();

        me.fireEvent("notify:moduleLoader.finishedLoadingModules");
    },

    /**
     * Load all modules.
     *
     * @private
     * @param {Object} modulesConfig Modules config.
     * @returns {void}
     */
    loadModules: function(modulesConfig) {
        var me = this,
            currentPlatform = Environment.getCurrentPlatform(),
            isLegacyService,
            skipModule,
            Ref;

        me.modules = {};

        // Process all modules
        Sys.iterate(modulesConfig, function(name, config) {
            skipModule = (Sys.isArray(config.platforms) && config.platforms.indexOf(currentPlatform) === -1);

            // If the module does not already exist, create it
            if (!Sys.isDefined(me.modules[name]) && !skipModule) {
                // Fetch the class reference from the string
                Ref = me.getClassFromString(config.module);

                // If the module could not be found and it is optional, skip it
                if (!Sys.isDefined(Ref) && config.optional === true) {
                    return;
                }

                // Create the module
                me.modules[name] = new Ref({
                    name: name,
                    model: me.getPlatformSpecificClass(config.model),
                    view: me.getPlatformSpecificClass(config.view),
                    controller: me.getClassFromString(config.controller)
                });

                // If the module should be accessible through a global namespace object
                if (typeof config.global === "string") {
                    isLegacyService = config.global.toLowerCase() === "services";

                    if (!Sys.isDefined(window[config.global])) {
                        window[config.global] = {};
                    }
                    window[config.global][name] = me.modules[name].controller;

                    if (isLegacyService) {
                        window[config.global]["legacy"][name] = me.modules[name].controller;
                    }
                }

                me.applyMixins(me.modules[name], me.combineMixins(me.modules[name].getMixinDependencies(), config.mixins));
            }
        });
    },

    /**
     * Combine the mixins specified in the module with the ones from the config.
     *
     * @example
     *     combineMixins(
     *         [
     *             "animation",
     *             {
     *                 "userInput" : {
     *                     "listenToUserInput" : true
     *                 }
     *             }
     *         ],
     *         [ "orientation" ]
     *     );
     *
     * @private
     * @param {Array} moduleMixins Contains mixins in string or object format.
     * @param {string[]} configMixins Contains mixins in a string array.
     * @returns {Object} The object where keys are mixin names and values are optional configurations.
     */
    combineMixins: function(moduleMixins, configMixins) {
        var mixed = {},
            mixin,
            config,
            key,
            i,
            mixinArray,
            numMixins,
            currentPlatform = Environment.getCurrentPlatform();

        if (Sys.isArray(moduleMixins)) {
            mixinArray = moduleMixins;
            numMixins = moduleMixins.length;
        }
        else {
            // Check if mixin config contains current platform or use default
            mixinArray = (moduleMixins.hasOwnProperty(currentPlatform)) ? moduleMixins[currentPlatform] : moduleMixins.fallback;
            numMixins = (mixinArray && mixinArray.length) || 0;
        }

        // Append all the module mixins
        for (i = -1; ++i < numMixins;) {
            mixin = mixinArray[i];

            if (Sys.isObj(mixin)) {
                key = Object.keys(mixin)[0];
                config = mixin[key];

                mixed[key] = config;
            }
            else if (typeof mixin === "string") {
                mixed[mixin] = undefined;
            }
        }

        // Append the mixins that are not already in place
        if (Sys.isArray(configMixins)) {
            numMixins = configMixins.length;

            for (i = -1; ++i < numMixins;) {
                mixin = configMixins[i];

                if (!Sys.isDefined(mixed[mixin])) {
                    mixed[mixin] = undefined;
                }
            }
        }

        return mixed;
    },

    /**
     * Apply the mixin methods to the module,
     * executeOnInstantiation methods are run instead of both being done one class at a time.
     *
     * @private
     * @param {Core.Module} module The module instance.
     * @param {Object} mixins The object containing the mixins to use.
     * @returns {void}
     */
    applyMixins: function(module, mixins) {
        var classes = ["controller", "model", "view"],
            keys = Object.keys(mixins),
            numMixins = keys.length,
            numClasses = classes.length,
            mixin, className, config, key,
            i, j;

        for (i = -1; ++i < numMixins;) {
            key = keys[i];
            config = mixins[key] || {};
            mixin = Mixins[key];

            if (Sys.isDefined(mixin)) {
                // Check for additions to each class type
                for (j = -1; ++j < numClasses;) {
                    className = classes[j];

                    // Append the additions
                    if (Sys.isDefined(mixin[className]) && Sys.isDefined(module[className]) && Sys.isDefined(mixin[className].methods)) {
                        this.applyMixinProperties(module[className], mixin[className].methods);
                    }
                }
                for (j = -1; ++j < numClasses;) {
                    className = classes[j];

                    // Append the additions
                    if (Sys.isDefined(mixin[className]) && Sys.isDefined(module[className]) && Sys.isDefined(mixin[className].executeOnInstantiation)) {
                        this.executeMixinProperties(module[className], mixin[className].executeOnInstantiation, config);
                    }
                }
            }

            /* BUILD_START */
            else {
                console.error("ModuleLoader :: The mixin (" + key + ") specified does not exist");
            }

            /* BUILD_END */
        }
    },

    /**
     * Apply the mixin properties that does not already exist.
     *
     * @example
     *     var A = { b : function() {return "Hello world!";} };
     *     applyMixinProperties(A, { a : function(){} });
     *
     *     console.log(A);
     *     > { a : function() {}, b : function() {return "Hello world!";} }
     *
     * @private
     * @param {Object} obj The object to append the properties on.
     * @param {Object} append The object containing the properties to append.
     * @returns {void}
     */
    applyMixinProperties: function(obj, append) {
        var keys = Object.keys(append),
            numKeys = keys.length,
            key,
            i = 0;

        while (i < numKeys) {
            key = keys[i];

            if (!Sys.isDefined(obj[key])) {
                obj[key] = append[key];
            }
            ++i;
        }
    },

    /**
     * Execute the provided methods in the given scope.
     *
     * @private
     * @param {Object} scope The scope to execute the methods in.
     * @param {Object} executables The methods to execute.
     * @param {Object} [config] The config to pass into the methods.
     * @returns {void}
     */
    executeMixinProperties: function(scope, executables, config) {
        var methods = Object.keys(executables),
            numMethods = methods.length,
            i = 0;

        while (i < numMethods) {
            executables[methods[i]].call(scope, config);
            ++i;
        }
    },

    /**
     * Update the states in the state handler based on the information given in each module.
     *
     * @example
     *     //Object from Module.getStateChanges() are returned in the following format:
     *
     *     //New state:
     *
     *     {
     *         "bonusGameState" : {
     *              state : {
     *                  name : "BonusGameState",
     *                  execute : function(env) {},
     *                  waitEvents : {}
     *              }
     *         }
     *     }
     *
     *     //Existing state:
     *
     *     {
     *         "winPresentation" : {
     *              queue : [
     *                  function(){}
     *              ],
     *              waitEvents : [
     *                  "firstEventName",
     *                  "secondEventName"
     *              ]
     *         }
     *     }
     *
     * @private
     * @returns {void}
     */
    updateStates: function() {
        var me = this,
            modules = me.modules,
            existingStates = me.stateHandler.states;

        me.moduleQueues = {};

        // Add all new states
        Sys.iterate(modules, function(moduleName, moduleObject) {
            // Get module specific state changes, if any
            var changes = moduleObject.getStateChanges();

            Sys.iterate(changes, function(state, config) {
                var waitEvents = [];

                // Add new states to the states index
                if (Sys.isDefined(config.state)) {
                    me.stateHandler.addState(state, config.state);

                    Sys.iterate(config.state.waitEvents, function(eventName) {
                        waitEvents.push(eventName);
                    });

                    if (waitEvents.length > 0) {
                        // Relay events to the state handler
                        me.registerListenersOnStateHandler(moduleObject.controller, waitEvents);
                    }
                }
            });
        });

        // Add all state modifications
        Sys.iterate(modules, function(moduleName, moduleObject) {
            // Get module specific state changes, if any
            var changes = moduleObject.getStateChanges();

            Sys.iterate(changes, function(state, config) {
                // Add state modification if the state to be modified exists
                if (!Sys.isDefined(config.state) && Sys.isDefined(existingStates[state])) {
                    // Wait events
                    if (Sys.isArray(config.waitEvents)) {
                        // Add wait-for events to already existing states
                        Sys.each(config.waitEvents, function(event) {
                            existingStates[state].waitEvents[event] = false;
                        });
                        // Relay events to the state handler
                        me.registerListenersOnStateHandler(moduleObject.controller, config.waitEvents);
                    }

                    // Queue
                    if (Sys.isArray(config.queue)) {
                        // Insert the execute function in the queue for the given state
                        me.updateModuleQueue(moduleName, existingStates[state].name, config.queue);
                    }
                }

                /* BUILD_START */
                else if (!Sys.isDefined(existingStates[state])) {
                    console.warn("ModuleLoader.updateStates :: The state (" + state + ") you try to modify does not exist. Modified by " + moduleName);
                }

                /* BUILD_END */
            });
        });
    },

    /**
     * Setup the event listeners on the state handler.
     *
     * @private
     * @param {Object} source The source that fires the event.
     * @param {string[]} events The events to relay.
     * @returns {void}
     */
    registerListenersOnStateHandler: function(source, events) {
        var numEvents = events.length,
            i;

        // Only register the event if it is not already being listened to
        for (i = -1; ++i < numEvents;) {
            if (!this.stateHandler.hasListener(events[i])) {
                this.stateHandler.registerEvents(events[i]);
            }
        }
    },

    /**
     * Add the module queue to the state order.
     *
     * @private
     * @param {string} moduleName The name of the module.
     * @param {string} stateName The name of the state.
     * @param {Array} queue The functions to run.
     * @returns {void}
     */
    updateModuleQueue: function(moduleName, stateName, queue) {
        var state = this.stateOrder[stateName],
            index;

        if (Sys.isDefined(state)) {
            index = state.indexOf(moduleName);

            if (index >= 0) {
                state.splice.apply(state, [index, 1].concat(queue));

                this.stateOrder[stateName] = state;
            }
            else {
                console.warn("ModuleLoader.updateModuleQueue :: The module (" + moduleName + ") is not specified in the module loader for the current state (" + stateName + ")");
            }
        }
        else {
            console.warn("ModuleLoader.updateModuleQueue :: The state (" + stateName + ") is not specified in the module loader");
        }
    },

    /**
     * Remove unused modules from the state queue.
     *
     * @private
     * @returns {void}
     */
    cleanStateQueue: function() {
        var me = this;

        Sys.iterate(me.stateOrder, function(stateName, queue) {
            var i;

            for (i = 0; i < queue.length; i++) {
                if (typeof queue[i] === "string" || !Sys.isDefined(queue[i])) {
                    queue.splice(i, 1);
                    i--;
                }
            }
        });
    },

    /**
     * Add game specific state order to the generic one.
     *
     * @private
     * @returns {void}
     */
    addGameSpecificStateOrder: function() {
        var me = this,
            stateOrder = me.stateOrder,
            config = Resources.readData("config"),
            gameStateOrder = config.stateOrder;

        // If we have game specific states
        if (Sys.isDefined(gameStateOrder)) {
            // Process each state
            Sys.iterate(gameStateOrder, function(stateName, stateConfig) {
                Sys.each(stateConfig, function(item) {
                    var index;

                    // If the state is not defined in the generic list add it
                    if (!Sys.isDefined(stateOrder[stateName])) {
                        stateOrder[stateName] = item.modules;
                    }
                    // If we have a place to put the new modules
                    else if (Sys.isDefined(item.insertBefore)) {
                        index = stateOrder[stateName].indexOf(item.insertBefore) + 1;

                        // If we should add them first
                        if (index < 0) {
                            stateOrder[stateName] = item.modules.concat(stateOrder[stateName]);
                        }
                        // Add them at the correct position
                        else {
                            stateOrder[stateName].splice.apply(stateOrder[stateName], [index, 0].concat(item.modules));
                        }
                    }
                    // Add them last
                    else {
                        stateOrder[stateName].push.apply(stateOrder[stateName], item.modules);
                    }
                });
            });
        }
    },

    /**
     * Get the class reference based on the string.
     *
     * @private
     * @param {string|Function} className The class name (including scope).
     * @returns {Function|undefined} The reference to the class.
     */
    getClassFromString: function(className) {
        var parts,
            classReference,
            i,
            numParts;

        if (typeof className === "string") {
            parts = className.split(".");
            classReference = window;
            numParts = parts.length;

            for (i = 0; i < numParts; i++) {
                if (Sys.isDefined(classReference)) {
                    classReference = classReference[parts[i]];
                }
            }

            return classReference;
        }
        else if (typeof className === "function") {
            return className;
        }

        return undefined;
    },

    getPlatformSpecificClass: function(MVCConfig) {
        var me = this,
            MVC = MVCConfig;

        // Check if MVC is an object (platform specific MVCs)
        if (Sys.isObj(MVC)) {
            // Evaluate so the MVC object only contains MVCs for supported platforms
            if (me.isMVCSupported(MVC)) {
                // get MVC based on platform and available MVCs for this module
                MVC = me.getPlatformView(MVC);
            }
            else {
                console.error("ModuleLoader.getPlatformSpecificClass : MVCs for non-supported platform provided");
            }
        }

        return me.getClassFromString(MVC);
    },

    /**
     * @private
     * @param {Object} MVCObj MVC object.
     * @returns {boolean} Whether or not MVC is supported or not.
     */
    isMVCSupported: function(MVCObj) {
        // get all supported platforms
        var supportedPlatforms = [
                "mobile",
                "mobileLow",
                "tablet",
                "tabletLow",
                "desktop"
            ],

            // flag that indicate if the MVC object only contains supported platforms
            isOK = true;

        // evaluate
        Sys.iterate(MVCObj, function(value, key) {
            if (supportedPlatforms.indexOf(value) < 0) {
                isOK = false;
            }
        });

        return isOK;
    },

    getPlatformView: function(viewObj) {
        var currentPlatform = Environment.getCurrentPlatform();

        // If we are on a desktop but we do not have a desktop view, use tablet
        if (currentPlatform === "desktop" && !Sys.isDefined(viewObj[currentPlatform])) {
            currentPlatform = "tablet";
        }

        // If we are on a tablet but we do not have a tablet view, use mobile
        if (currentPlatform === "tablet" && !Sys.isDefined(viewObj[currentPlatform])) {
            currentPlatform = "mobile";
        }

        // If we are on a tabletLow but we do not have a tabletLow view, use mobileLow
        if (currentPlatform === "tabletLow" && !Sys.isDefined(viewObj[currentPlatform])) {
            currentPlatform = "mobileLow";
        }

        // If we are on a mobileLow but we do not have a mobileLow view, use mobile
        if (currentPlatform === "mobileLow" && !Sys.isDefined(viewObj[currentPlatform])) {
            currentPlatform = "mobile";
        }

        return viewObj[currentPlatform];
    },

    /**
     * Get the common state order.
     *
     * @private
     * @returns {Object} The object containing the state order.
     */
    getCommonStateOrder: function() {
        return {
            BeforeLoaderClose: [
                "stage"
            ],
            Idle: [
                "playForRealPromo"
            ]
        };
    }
};

Core.ModuleLoader = Sys.extend(Sys.Observable, Core.ModuleLoader, "Core.ModuleLoader");
